import { FaPlaneArrival, FaHome } from 'react-icons/fa';
import heroImage from '../../../assets/images/service-airport.webp';
import { CANNOT_SHIP } from './shared';
import type { ServiceContent } from './types';

const airport: ServiceContent = {
  slug: 'airport',
  breadcrumbName: 'Airport to Airport',
  meta: {
    title: 'Airport to Airport Air Cargo from Saudi Arabia | Sahari Cargo',
    description:
      'Air cargo from Saudi Arabia to the destination airport, with import customs cleared by us. The recipient collects from the airport cargo terminal. Get a quote.',
  },
  heroImage,
  heroAlt: 'Air cargo being loaded at the airport',
  h1: 'Airport to Airport Air Cargo Service',
  intro: [
    'Airport to airport is air cargo without the final delivery leg. Your shipment flies from Saudi Arabia to the destination airport, we clear it through import customs there, and the recipient collects it from the airport cargo terminal. It suits shippers who want the speed of air freight, and recipients who are happy — or prefer — to pick it up themselves.',
  ],
  sections: [
    {
      heading: 'What Airport to Airport Means With Us',
      paragraphs: [
        "With some providers, \"airport to airport\" means the cargo lands and the recipient is on their own with customs. Not with us. We prepare the export documents in Saudi Arabia, book the flight, and handle import clearance at the destination. The only thing left for the recipient is collecting the cleared shipment from the airport cargo terminal.",
      ],
    },
    {
      heading: 'Airport to Airport vs Door to Door',
      cards: [
        {
          icon: FaPlaneArrival,
          title: 'Airport to Airport',
          text: 'We fly the cargo and clear customs at the destination. The recipient collects it from the airport cargo terminal. A good fit when the recipient is near the airport, or for business shipments they want to collect themselves.',
        },
        {
          icon: FaHome,
          title: 'Door to Door',
          text: "We collect from your address in Saudi Arabia and deliver all the way to the recipient's door, by air or sea. The best fit when the recipient can't get to the airport, or you want the whole journey handled.",
          link: { to: '/services/door-to-door', label: 'See door-to-door delivery' },
        },
      ],
    },
    {
      heading: 'How Our Airport to Airport Service Works',
      steps: [
        { title: 'Book your shipment', text: 'Share the cargo details, destination airport, and recipient.' },
        { title: 'Hand over the cargo', text: 'Drop it at a Sahari Cargo branch, or ask us to collect it.' },
        { title: 'Export and flight', text: 'We prepare the export documents and send it on a flight to the destination airport.' },
        { title: 'Import clearance by us', text: 'We clear the shipment through customs at the destination.' },
        { title: 'Recipient collects', text: 'We let the recipient know it is ready, and they collect from the airport cargo terminal.' },
      ],
    },
    {
      heading: 'Who Airport to Airport Suits',
      bullets: [
        'Businesses sending stock or samples to a trading partner who handles their own pickup',
        'Larger or bulk shipments where the recipient arranges their own transport from the airport',
        'Recipients who live or work close to the destination airport',
        'Anyone who wants air freight speed without paying for final delivery',
      ],
    },
    {
      heading: 'Destinations We Fly To',
      paragraphs: [
        "We run airport to airport air cargo from Saudi Arabia to India, the Philippines, Bangladesh, Pakistan, Nepal, Sri Lanka, Malaysia, Singapore, Brunei, and the United Kingdom. If your destination isn't listed, contact us — we can usually confirm the same day.",
      ],
      countries: true,
      warning: CANNOT_SHIP,
    },
    {
      heading: 'Why Choose Sahari Cargo for Air Cargo',
      paragraphs: [
        "Because customs at the destination is our job, not the recipient's. Your cargo is booked, flown, and cleared by one team, tracked from the moment you hand it over, and backed by 12 branches in Saudi Arabia you can call or walk into if you need an answer.",
      ],
    },
  ],
  faqs: [
    {
      q: 'What is the difference between airport to airport and door to door?',
      a: "With airport to airport, we fly the cargo and clear customs at the destination, and the recipient collects it from the airport cargo terminal. With door to door, we also collect from your address in Saudi Arabia and deliver to the recipient's address.",
    },
    {
      q: 'Does the recipient need to handle customs clearance?',
      a: 'No. Import clearance at the destination airport is handled by us. The recipient only needs to collect the cleared shipment.',
    },
    {
      q: 'Where does the recipient collect the shipment?',
      a: "From the cargo terminal at the destination airport. We let the recipient know when the shipment has been cleared and is ready to collect.",
    },
    {
      q: 'What does the recipient need to bring to collect?',
      a: "A valid photo ID and the shipment's reference number. Requirements can vary by airport, so we confirm exactly what's needed when we tell the recipient the cargo is ready.",
    },
    {
      q: 'How long does airport to airport air cargo take?',
      a: 'It depends on the destination and flight availability at the time of booking. Request a quote and we will give you the current timeline for your route.',
    },
  ],
  cta: {
    title: 'Sending by air?',
    text: "Tell us the destination airport and what you're shipping, and we'll give you a quote.",
  },
};

export default airport;
